"use strict";

if (!customElements.get("bundle-builder")) {
  customElements.define(
    "bundle-builder",
    class BundleBuilder extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        /** Selectors */
        /** @type {HTMLDivElement} */
        this.priceEl = document.querySelector("[data-dynamic-price]");
        /** @type {HTMLParagraphElement} */
        this.choicesRemainingTextEl = this.querySelector(
          "[data-choices-remaining-text]",
        );
        /** @type {string} */
        this.choicesRemainingText = window.bundles.choicesRemainingText;
        /** @type {number} */
        this.choicesRemainingCount = parseInt(
          this.dataset.choicesRemainingCount,
          10,
        );
        /** @type {string} */
        this.bundleReadyText = window.bundles.bundleReadyText;
        /** @type {number} */
        this.discountAmount = parseInt(this.dataset.discountAmount, 10);
        /** @type {HTMLInputElement[]} */
        this.componentSelectorInputs = this.querySelectorAll(
          "input[data-component-quantity]",
        );
        /** @type {HTMLInputElement} */
        this.hiddenInput = this.querySelector(
          "input[name='properties[_components]']",
        );
        /** @type {HTMLButtonElement} */
        this.addToCart = this.querySelector('button[type="submit"]');
        /** @type {HTMLFormElement} */
        this.form = this.querySelector("form");

        /** Set up event listener for add to cart */
        this.form.addEventListener("submit", (e) => this.handleAddToCart(e));

        /** Set up event listener for changing variant */
        this.addEventListener("change", (e) => {
          const selectedVariantRadio = e.target.closest('input[name="id"]');
          if (!selectedVariantRadio) return;
          this.reloadBundleBuilder(selectedVariantRadio);
        });

        /** Set up event listner for inputs */
        this.addEventListener(
          "change",
          debounce((e) => {
            const input = e.target.closest("input[data-component-quantity]");
            if (!input) return;

            const oldValue = parseInt(input.dataset.prevValue || 0, 10);
            const newValue = parseInt(input.value || 0, 10);
            const diff = newValue - oldValue;
            const componentContainer = input.closest(
              ".bundle-builder_component",
            );

            // check if user is trying to increase when they can't
            if (
              (diff > 0 && this.choicesRemainingCount <= 0) ||
              diff > this.choicesRemainingCount
            ) {
              input.value = oldValue;
              return;
            }

            input.dataset.prevValue = newValue;

            if (newValue > 0) {
              componentContainer.classList.add("has-value");
            } else if (newValue == 0) {
              componentContainer.classList.remove("has-value");
            }

            if (diff > 0) {
              // Value has increased
              this.decreaseChoiceCount(diff);
            } else if (diff < 0) {
              // Value has decreased
              this.increaseChoiceCount(Math.abs(diff));
            }

            this.updateChoiceText();
            this.updateHiddenInput();
          }),
        );

        /** Set up event listener for quantity buttons */
        this.addEventListener("click", (e) => {
          const btn = e.target.closest("[data-component-quantity-button]");
          if (!btn) return;

          const container = btn.closest(".bundle-builder_component");
          /** @type {HTMLInputElement} */
          const input = container?.querySelector("[data-component-quantity]");
          if (!input) return;

          const current = parseInt(input.value, 10) || 0;
          let newValue = current;

          if (btn.dataset.componentQuantityButton === "minus") {
            newValue = Math.max(0, current - 1); // Prevent going below 0
          } else if (btn.dataset.componentQuantityButton === "plus") {
            newValue = current + 1;
          }

          if (newValue !== current) {
            input.value = newValue;
            input.dispatchEvent(new Event("change", { bubbles: true }));
          }
        });
      }

      async reloadBundleBuilder(selectedVariantRadio) {
        const container = document.querySelector(".bundle-builder_parent");
        const variantId = selectedVariantRadio.value;
        const url = new URL(window.location.href);
        url.searchParams.set("section_id", "bundles-pdp");
        url.searchParams.set("variant", variantId);

        this.classList.add("loading");
        let res;
        let html;

        try {
          res = await fetch(url);

          if (!res.ok)
            throw new Error(`Fetch failed with status ${res.status}`);

          html = await res.text();

          const temp = document.createElement("div");
          temp.innerHTML = html;

          container.innerHTML = temp.querySelector(
            ".bundle-builder_parent",
          ).innerHTML;

          url.searchParams.delete("section_id");
          window.history.pushState({}, "", url);
        } catch (error) {
          console.error("Error when reloading the bundle builder:", error);
        } finally {
          this.classList.remove("loading");
          StampedFn.reloadUGC();
        }
      }

      increaseChoiceCount(diff = 1) {
        this.choicesRemainingCount = this.choicesRemainingCount + diff;
      }

      decreaseChoiceCount(diff = 1) {
        this.choicesRemainingCount = this.choicesRemainingCount - diff;
      }

      updateChoiceText() {
        if (this.choicesRemainingCount == 0) {
          // All choices made
          this.choicesRemainingTextEl.textContent = this.bundleReadyText;
          this.toggleAddToCart(false);
          return;
        }
        this.toggleAddToCart();

        this.choicesRemainingTextEl.textContent = `${this.choicesRemainingCount} ${this.choicesRemainingText}`;
      }

      updateHiddenInput() {
        const result = [];
        this.componentSelectorInputs.forEach((input) => {
          const component = input.dataset.component;
          const count = parseInt(input.value, 10);

          if (count < 1 || isNaN(count)) return;

          result.push(...Array(count).fill(component));
        });

        this.hiddenInput.value = result.join(",");
        this.updatePrice();
      }

      toggleAddToCart(disabled = true) {
        this.addToCart.disabled = disabled;
      }

      updatePrice() {
        const items = this.hiddenInput.value.split(",");
        const totalPrice = items.reduce((acc, item) => {
          const input = Array.from(this.componentSelectorInputs).find(
            (input) => input.dataset.component === item,
          );
          if (input) {
            acc += Number(input.dataset.componentPrice);
          }
          return acc;
        }, 0);

        const discount = (totalPrice * this.discountAmount) / 100;
        const finalPrice = totalPrice - discount;

        // format the price
        const formattedPrice = new Intl.NumberFormat(
          `${window.Shopify.locale}-${window.Shopify.country}`,
          {
            style: "currency",
            currency: window.Shopify.currency.active,
          },
        ).format(finalPrice / 100);

        // insert the price into the DOM, if the bundle is ready
        if (this.choicesRemainingCount === 0) {
          this.priceEl.textContent = formattedPrice;
        } else {
          this.priceEl.innerHTML = `<span class="bundle-builder_price-pending">${window.bundles.pricePending}</span>`;
        }
      }

      async handleAddToCart(e) {
        e.preventDefault();

        const items = this.hiddenInput.value.split(",");
        // Collapse repeated items into one cartItem per id with correct quantity
        const itemMap = {};
        items.forEach((item) => {
          if (!itemMap[item]) {
            itemMap[item] = 0;
          }
          // Find the input for this component to get its value
          const input = Array.from(this.componentSelectorInputs).find(
            (input) => input.dataset.component === item,
          );
          if (input) {
            itemMap[item] = Number(input.value);
          }
        });

        const time = new Date().getTime();
        const cartItems = {
          items: Object.entries(itemMap).map(([id, quantity]) => ({
            id: Number(id),
            quantity: Number(quantity),
          })),
        };

        console.log("Submitting bundle builder: ", JSON.stringify(cartItems));

        // Use the same pattern as product-form.js
        const cartDrawer = document.querySelector("cart-drawer");
        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData();
        cartItems.items.forEach((item, i) => {
          formData.append(`items[${i}][id]`, item.id);
          formData.append(`items[${i}][quantity]`, item.quantity);
          if (item.properties) {
            Object.entries(item.properties).forEach(([key, value]) => {
              formData.append(`items[${i}][properties][${key}]`, value);
            });
          }
        });
        if (cartDrawer)
          formData.append(
            "sections",
            cartDrawer.getSectionsToRender().map((section) => section.id),
          );
        formData.append("sections_url", window.location.pathname);
        config.body = formData;

        const response = await fetch(routes.cart_add_url, config).then((r) =>
          r.json(),
        );

        if (response.status) {
          // Optionally handle error UI here
          console.error(response.description || response.message);
          return;
        }

        if (cartDrawer) {
          cartDrawer.renderContents(response);
        }
      }
    },
  );
}

if (!customElements.get("bundle-builder-step")) {
  customElements.define(
    "bundle-builder-step",
    class BundleBuilderStep extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.nextStepButton = this.querySelector("[data-next-step]");

        // Inputs explicitly marked as required
        this.requiredInputs = this.querySelectorAll("input:required");

        this.updateNextStepButtonState();

        // Listen to change events on all required inputs
        this.requiredInputs.forEach((input) => {
          input.addEventListener("change", () =>
            this.updateNextStepButtonState(),
          );
          input.addEventListener("input", () =>
            this.updateNextStepButtonState(),
          );
        });

        /** Set up event listener for next step buttons */
        this.addEventListener("click", (e) => {
          const btn = e.target.closest("[data-next-step]");
          if (!btn) return;
          this.goToNextStep();
        });

        /** Set up event listener for back step buttons */
        this.addEventListener("click", (e) => {
          const btn = e.target.closest("[data-back-step]");
          if (!btn) return;
          this.goToPreviousStep();
        });
      }

      updateNextStepButtonState() {
        if (!this.nextStepButton) return;

        const allSatisfied = Array.from(this.requiredInputs).every((input) => {
          if (input.type === "radio") {
            // Only validate one radio per group
            const groupName = input.name;
            const groupInputs = this.querySelectorAll(
              `input[type="radio"][name="${groupName}"]`,
            );
            return Array.from(groupInputs).some((radio) => radio.checked);
          }

          if (input.type === "number") {
            const value = parseInt(input.value, 10);
            return !isNaN(value) && value > 0;
          }

          // Add more input types as needed
          return input.value.trim() !== "";
        });

        this.nextStepButton.disabled = !allSatisfied;
      }

      goToNextStep() {
        this.removeAttribute("data-step-visible");

        const allSteps = Array.from(
          document.querySelectorAll("bundle-builder-step"),
        );
        const currentIndex = allSteps.indexOf(this);

        if (currentIndex < allSteps.length - 1) {
          const nextStep = allSteps[currentIndex + 1];
          nextStep.setAttribute("data-step-visible", "true");
        }
      }

      goToPreviousStep() {
        this.removeAttribute("data-step-visible");

        const allSteps = Array.from(
          document.querySelectorAll("bundle-builder-step"),
        );
        const currentIndex = allSteps.indexOf(this);

        if (currentIndex > 0) {
          const previousStep = allSteps[currentIndex - 1];
          previousStep.setAttribute("data-step-visible", "true");
        }
      }
    },
  );
}
